/** SUNTRONIX ONE — Customer Hub → Our Contracts (AMC/PMS tenant view). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { FileSignature, CalendarClock, CheckCircle2, ShieldCheck } from "lucide-react";

const TYPE_TINT = {
  AMC: "bg-teal-100 text-teal-800", WARRANTY_SUPPORT: "bg-blue-100 text-blue-800",
  PREVENTIVE_MAINTENANCE: "bg-emerald-100 text-emerald-800",
  MANAGED_SERVICE: "bg-violet-100 text-violet-800", SUBSCRIPTION: "bg-amber-100 text-amber-800",
  RETAINER: "bg-slate-100 text-slate-700",
};

export default function OurContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/amc/customer/contracts")
      .then(({ data }) => setContracts(data.contracts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-[1100px] space-y-4" data-testid="our-contracts-page">
      <header className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><FileSignature className="h-5 w-5" /></div>
        <div>
          <div className="font-heading text-xl font-semibold">Our Contracts</div>
          <div className="text-[11px] text-slate-500">AMC, preventive maintenance &amp; support coverage</div>
        </div>
      </header>

      {loading ? (
        <div className="p-8 text-center text-[12px] text-slate-400">Loading…</div>
      ) : !contracts.length ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-[12px] text-slate-400" data-testid="our-contracts-empty">
          No active contracts on record.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {contracts.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`our-contract-${c.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-400">{c.contract_number}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${TYPE_TINT[c.contract_type] || "bg-slate-100"}`}>{c.contract_type?.replace(/_/g, " ")}</span>
                <span className={`ml-auto rounded px-1.5 py-0.5 text-[9px] font-semibold ${c.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{c.status}</span>
              </div>
              <div className="mt-1 text-[14px] font-semibold text-slate-900">{c.title || c.coverage || c.contract_type}</div>
              {c.coverage && <div className="text-[11px] text-slate-500">{c.coverage}</div>}

              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
                  <span className="text-slate-500">SLA</span>
                  <span className="ml-auto font-semibold text-slate-800">{c.sla || "—"}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-slate-500">Renewal</span>
                  <span className="ml-auto font-semibold text-slate-800">{(c.renewal_date || "").slice(0, 10)}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1.5" data-testid={`our-contract-upcoming-${c.id}`}>
                  <CalendarClock className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-slate-500">Next visit</span>
                  <span className="ml-auto font-semibold text-slate-800">{c.upcoming_visit ? c.upcoming_visit.slice(0, 10) : "—"}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-slate-500">Completed</span>
                  <span className="ml-auto font-semibold text-slate-800">{c.completed_visits}/{c.total_visits}</span>
                </div>
              </div>
              {c.days_to_expiry != null && c.days_to_expiry <= 90 && c.days_to_expiry >= 0 && (
                <div className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-800" data-testid={`our-contract-renewal-alert-${c.id}`}>
                  Renewal due in {c.days_to_expiry} days
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
